import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, LinearProgress, Tooltip, Typography } from '@mui/material';
import { getMilitaryRankName, getMilitaryRankStage } from '../utils/militaryRankUtils';
import LevelIcon from './LevelIcon';
import '../styles/FloatingXPBar.css';

const FloatingXPBar = ({ gameProfile, gameConfig, onClick, hasUnreadTodayYou = false }) => {
  const { t } = useTranslation();
  const [showXPGain, setShowXPGain] = useState(false);
  const [xpGain, setXpGain] = useState(0);
  const [prevTotalXp, setPrevTotalXp] = useState(0);

  // 计算当前等级
  const getCurrentLevel = () => {
    if (!gameProfile || !gameConfig?.levelThresholds) return 1;
    
    const thresholds = gameConfig.levelThresholds;
    let actualLevel = 1;
    for (let i = 0; i < thresholds.length; i++) {
      if (gameProfile.totalXp >= thresholds[i]) {
        actualLevel = i + 2;
      } else {
        break;
      }
    }
    return actualLevel;
  };

  // 计算经验值进度
  const getXPProgress = () => {
    if (!gameProfile || !gameConfig?.levelThresholds) return { percentage: 0, current: 0, next: 0 };
    
    const level = getCurrentLevel();
    const thresholds = gameConfig.levelThresholds;
    
    // 当前等级的起始经验值
    const currentLevelXP = level > 1 ? (thresholds[level - 2] || 0) : 0;
    // 下一等级所需经验值
    const nextLevelXP = level - 1 < thresholds.length ? thresholds[level - 1] : thresholds[thresholds.length - 1];
    
    // 当前进度
    const currentXP = gameProfile.totalXp - currentLevelXP;
    const requiredXP = nextLevelXP - currentLevelXP;
    const percentage = (currentXP / requiredXP) * 100;
    
    return {
      percentage: Math.min(percentage, 100),
      current: Math.round(currentXP),
      next: Math.round(requiredXP)
    };
  };

  // 监听经验值变化
  useEffect(() => {
    if (gameProfile && prevTotalXp > 0 && gameProfile.totalXp > prevTotalXp) {
      const gain = Math.round(gameProfile.totalXp - prevTotalXp);
      setXpGain(gain);
      setShowXPGain(true);
      
      // 3秒后隐藏增益提示
      setTimeout(() => {
        setShowXPGain(false);
      }, 3000);
    }
    
    if (gameProfile) {
      setPrevTotalXp(gameProfile.totalXp);
    }
  }, [gameProfile?.totalXp]);

  if (!gameProfile) return null;

  const level = getCurrentLevel();
  const rankInfo = getMilitaryRankStage(level);
  const rankName = getMilitaryRankName(level);
  const progress = getXPProgress();

  return (
    <Tooltip 
      title={
        <Box sx={{ p: 1 }}>
          <Typography variant="caption" sx={{ display: 'block', fontWeight: 'bold', mb: 0.5 }}>
            {rankInfo.stageName} · {rankName} ({t('floating_xp_level', { n: level })})
          </Typography>
          
          {/* 经验进度条 */}
          <Box sx={{ mb: 0.5 }}>
            <LinearProgress
              variant="determinate"
              value={progress.percentage}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: 'rgba(255, 255, 255, 0.2)',
                '& .MuiLinearProgress-bar': {
                  borderRadius: 2,
                  background: `linear-gradient(90deg, ${rankInfo.color}80, ${rankInfo.color})`
                }
              }}
            />
          </Box>
          
          <Typography variant="caption" sx={{ display: 'block', fontSize: '11px' }}>
            {t('floating_xp_current_progress')}: {progress.current}/{progress.next} XP ({Math.round(progress.percentage)}%)
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', color: '#FFD700', fontSize: '11px' }}>
            {t('floating_xp_total')}: {Math.round(gameProfile.totalXp)} XP
          </Typography>
          <Typography variant="caption" sx={{ display: 'block', mt: 0.5, opacity: 0.7, fontSize: '10px' }}>
            {t('floating_xp_view_detail')}
          </Typography>
        </Box>
      }
      arrow
      placement="bottom"
    >
      <Box
        className={`floating-xp-bar-compact ${showXPGain ? 'xp-gain-animation' : ''}`}
        onClick={onClick}
        sx={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          padding: '4px 8px',
          backgroundColor: 'transparent', // 移除背景色
          borderRadius: '4px', 
          border: 'none', // 移除边框
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          minWidth: 'max-content',
          flexShrink: 0,
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)', // 极淡的悬停背景
            transform: 'translateY(-1px)',
          }
        }}
      >
        {/* 军衔阶段图标 */}
        <Box 
          sx={{ 
            fontSize: '16px', 
            lineHeight: 1, 
            display: 'flex', 
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          {rankInfo.stageIcon}
        </Box>
        
        {/* 军衔名称和总经验 - 垂直布局 */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '0px' }}>
          {/* 军衔名称 */}
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '11px',
              fontWeight: 600, 
              color: 'rgba(255, 255, 255, 0.95)',
              whiteSpace: 'nowrap',
              lineHeight: 1.1
            }}
          >
            {rankInfo.stageName}·{rankName}
          </Typography>
          
          {/* 总经验值 */}
          <Typography 
            variant="caption" 
            sx={{ 
              fontSize: '10px',
              fontWeight: 500, 
              color: 'rgba(255, 215, 0, 0.95)', 
              whiteSpace: 'nowrap',
              lineHeight: 1.1
            }}
          >
            {Math.round(gameProfile.totalXp)} XP
          </Typography>
        </Box>
        
        {/* 手术 3：今日你叙事卡片未读小红点（拉式入口的轻提示） */}
        {hasUnreadTodayYou && (
          <Tooltip title="镜子里的你想说一句话" arrow placement="bottom">
            <Box
              sx={{
                position: 'absolute',
                top: 2,
                right: 2,
                width: 8,
                height: 8,
                borderRadius: '50%',
                backgroundColor: '#a5b4fc',
                boxShadow: '0 0 6px rgba(165,180,252,0.8)',
                pointerEvents: 'auto',
                cursor: 'pointer',
                animation: 'todayYouPulse 2.4s ease-in-out infinite',
                '@keyframes todayYouPulse': {
                  '0%, 100%': { opacity: 0.7, transform: 'scale(1)' },
                  '50%': { opacity: 1, transform: 'scale(1.25)' },
                },
              }}
            />
          </Tooltip>
        )}

        {/* 经验增益提示 */}
        {showXPGain && (
          <Box
            className="xp-gain-indicator"
            sx={{
              position: 'absolute',
              top: -20,
              right: 10,
              color: '#FFD700',
              fontSize: '12px',
              fontWeight: 'bold',
              animation: 'floatUp 3s ease-out',
              pointerEvents: 'none',
              textShadow: '0 0 8px rgba(255, 215, 0, 0.8)'
            }}
          >
            +{xpGain} XP
          </Box>
        )}
      </Box>
    </Tooltip>
  );
};

export default FloatingXPBar;

